package diana.soleil.design.iraniancanadienbazzar.feature.addbusiness

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIconButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.appContentWidth
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_back_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_step_label
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_wizard_brand
import org.jetbrains.compose.resources.stringResource

const val WIZARD_TOTAL_STEPS = 5

/**
 * Shared chrome for the numbered wizard steps: a teal top bar (back + brand + optional trailing icon),
 * a "Step X of 5" progress header with a track, the step [title], a scrollable content area, and a
 * pinned [bottomBar] for the step's actions. Keeps every step visually identical to the Stitch flow.
 */
@Composable
fun WizardScaffold(
    stepIndex: Int,
    title: String,
    onBack: () -> Unit,
    bottomBar: @Composable () -> Unit,
    modifier: Modifier = Modifier,
    topBarTrailing: @Composable (() -> Unit)? = null,
    content: @Composable ColumnScope.() -> Unit,
) {
    Column(modifier = modifier.fillMaxHeight().background(AppTheme.colors.background).appContentWidth(AppTheme.sizes.contentMaxWidthCompact)) {
        // Teal top bar. statusBarsPadding ONLY — safeContentPadding also added the bottom-nav and
        // keyboard insets here, visibly bloating the bar (owner bug report 2026-07-26).
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .background(AppTheme.colors.primaryContainer)
                .statusBarsPadding()
                .padding(horizontal = AppTheme.spacing.lg, vertical = AppTheme.spacing.md),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
        ) {
            AppIconButton(
                iconName = "arrow_back",
                contentDescription = stringResource(Res.string.ab_back_a11y),
                onClick = onBack,
                tint = AppTheme.colors.onPrimary,
            )
            Text(
                text = stringResource(Res.string.ab_wizard_brand),
                style = AppTheme.typography.headlineMd,
                color = AppTheme.colors.onPrimary,
                modifier = Modifier.weight(1f),
            )
            if (topBarTrailing != null) topBarTrailing()
        }

        // Progress header
        Column(
            modifier = Modifier.padding(
                start = AppTheme.spacing.lg,
                end = AppTheme.spacing.lg,
                top = AppTheme.spacing.lg,
            ),
            verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
        ) {
            Text(
                text = stringResource(Res.string.ab_step_label, stepIndex, WIZARD_TOTAL_STEPS),
                style = AppTheme.typography.labelSm,
                color = AppTheme.colors.primary,
            )
            ProgressTrack(fraction = stepIndex.toFloat() / WIZARD_TOTAL_STEPS)
            Text(
                text = title,
                style = AppTheme.typography.headlineLg,
                color = AppTheme.colors.onSurface,
            )
        }

        // Scrollable step content
        Column(
            modifier = Modifier
                .weight(1f)
                .fillMaxWidth()
                .verticalScroll(rememberScrollState())
                .padding(AppTheme.spacing.lg),
            verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.lg),
            content = content,
        )

        // Pinned actions. navigationBarsPadding ONLY — safeContentPadding also added a status-bar-
        // height inset on top of the bar, and the gesture handle needs just the bottom inset.
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .background(AppTheme.colors.surface)
                .navigationBarsPadding()
                .imePadding()
                .padding(AppTheme.spacing.lg),
        ) { bottomBar() }
    }
}

@Composable
private fun ProgressTrack(fraction: Float) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(AppTheme.sizes.strokeSm)
            .background(AppTheme.colors.outlineVariant, AppTheme.shapes.pill),
    ) {
        Box(
            modifier = Modifier
                .fillMaxWidth(fraction.coerceIn(0f, 1f))
                .height(AppTheme.sizes.strokeSm)
                .background(AppTheme.colors.primaryContainer, AppTheme.shapes.pill),
        )
    }
}
